import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { ChevronLeft, Heart, Zap, CloudRain, Sun, Brain, Sparkles, Drama, Check } from 'lucide-react';

interface MoodOption {
  id: string;
  name: string;
  emoji: string;
  description: string;
  examples: string;
  icon: React.ReactNode;
  /** hue/sat/light for this mood's muted accent — kept subtle to fit the dark theme. Omit for the neutral "any" option, which uses the path's red accent. */
  hue?: number;
  sat?: number;
  light?: number;
}

const moods: MoodOption[] = [
  {
    id: 'feel-good',
    name: 'Feel-Good',
    emoji: '🎉',
    description: 'Uplifting stories that leave you feeling happy and inspired',
    examples: 'The Secret Life of Walter Mitty, Little Miss Sunshine, Chef',
    icon: <Heart className="h-6 w-6" />,
    hue: 43, sat: 82, light: 58,
  },
  {
    id: 'lighthearted',
    name: 'Lighthearted',
    emoji: '😆',
    description: 'Fun, easy-going entertainment with plenty of laughs',
    examples: 'The Princess Bride, School of Rock, Game Night',
    icon: <Sun className="h-6 w-6" />,
    hue: 28, sat: 80, light: 58,
  },
  {
    id: 'thought-provoking',
    name: 'Thought-Provoking',
    emoji: '🤯',
    description: 'Intellectually stimulating films that challenge your perspective',
    examples: 'Inception, Arrival, Ex Machina',
    icon: <Brain className="h-6 w-6" />,
    hue: 268, sat: 38, light: 66,
  },
  {
    id: 'suspenseful',
    name: 'Suspenseful',
    emoji: '😱',
    description: 'Edge-of-your-seat tension and thrilling moments',
    examples: 'Sicario, Wind River, Prisoners',
    icon: <Zap className="h-6 w-6" />,
    hue: 358, sat: 70, light: 58,
  },
  {
    id: 'dark-psychological',
    name: 'Dark & Psychological',
    emoji: '🖤',
    description: 'Complex psychological narratives with dark themes',
    examples: 'Gone Girl, Nightcrawler, The Girl with the Dragon Tattoo',
    icon: <CloudRain className="h-6 w-6" />,
    hue: 230, sat: 14, light: 62,
  },
  {
    id: 'gritty-raw',
    name: 'Gritty & Raw',
    emoji: '🎭',
    description: 'Raw, intense stories that don\'t hold back',
    examples: 'There Will Be Blood, No Country for Old Men, The Wrestler',
    icon: <Drama className="h-6 w-6" />,
    hue: 18, sat: 45, light: 50,
  },
  {
    id: 'any',
    name: 'No Mood Preference',
    emoji: '✨',
    description: 'Open to any type of movie',
    examples: 'Show me everything that matches my other preferences',
    icon: <Sparkles className="h-6 w-6" />,
  },
];

interface Props {
  selectedMoods: string[];
  onSelect: (moods: string[]) => void;
  onBack: () => void;
  showBackButton: boolean;
}

export function MoodSelection({ selectedMoods: initialSelectedMoods, onSelect, onBack, showBackButton }: Props) {
  const [selectedMoods, setSelectedMoods] = useState<string[]>(initialSelectedMoods);
  const [error, setError] = useState<string | null>(null);

  const handleMoodToggle = (moodId: string) => {
    setSelectedMoods(prev => {
      let newSelection: string[];

      if (moodId === 'any') {
        // If 'any' is selected, clear all other selections
        newSelection = prev.includes('any') ? [] : ['any'];
      } else {
        // If a specific mood is selected, remove 'any' if it's there
        newSelection = prev.filter(id => id !== 'any');

        if (prev.includes(moodId)) {
          newSelection = newSelection.filter(id => id !== moodId);
        } else if (newSelection.length < 2) { // Changed to 2 for max selection
          newSelection = [...newSelection, moodId];
        }
      }

      setError(null);
      return newSelection;
    });
  };

  const handleSubmit = () => {
    if (selectedMoods.length === 0) {
      // Default to 'any' if nothing is selected
      onSelect(['any']);
    } else {
      onSelect(selectedMoods);
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2 text-center">
        <h2 className="display text-2xl font-semibold sm:text-3xl">
          What's the <span className="text-gradient-dusk">mood</span> tonight?
        </h2>
        <p className="text-muted-foreground">
          Select up to 2 moods, or choose 'No Mood Preference' for a broader selection.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {moods.map((mood) => {
          const isSelected = selectedMoods.includes(mood.id);
          const isDisabled = selectedMoods.length >= 2 && !selectedMoods.includes(mood.id) && mood.id !== 'any';
          const hasTint = mood.hue !== undefined;
          const tint = hasTint ? `hsl(${mood.hue} ${mood.sat}% ${mood.light}%)` : undefined;

          return (
            <button
              key={mood.id}
              onClick={() => handleMoodToggle(mood.id)}
              disabled={isDisabled}
              className={cn(
                'select-card w-full p-5',
                isSelected && !hasTint && 'border-accent/70 bg-accent/[0.08] shadow-glow-dusk hover:border-accent hover:bg-accent/[0.1]',
                isDisabled && 'pointer-events-none opacity-40'
              )}
              style={isSelected && hasTint ? {
                borderColor: `hsl(${mood.hue} ${mood.sat}% ${mood.light}% / 0.55)`,
                backgroundColor: `hsl(${mood.hue} ${mood.sat}% ${mood.light}% / 0.08)`,
              } : undefined}
              aria-pressed={isSelected}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'shrink-0 rounded-2xl border border-white/[0.07] bg-white/[0.04] p-3 transition-colors duration-200',
                    !hasTint && (isSelected ? 'text-accent' : 'text-muted-foreground')
                  )}
                  style={hasTint ? { color: tint } : undefined}
                >
                  {mood.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        'font-semibold transition-colors duration-200',
                        !hasTint && (isSelected ? 'text-accent' : 'text-foreground')
                      )}
                      style={hasTint && isSelected ? { color: tint } : undefined}
                    >
                      {mood.emoji} {mood.name}
                    </span>
                    {isSelected && (
                      <span
                        className={cn(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                          !hasTint && 'bg-accent text-accent-foreground'
                        )}
                        style={hasTint ? { backgroundColor: tint, color: 'hsl(345 45% 7%)' } : undefined}
                      >
                        <Check className="h-4 w-4" strokeWidth={3} />
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {mood.description}
                  </p>
                  {mood.id !== 'any' && (
                    <p className="mt-2 text-xs italic tracking-wide text-muted-foreground/70">
                      e.g. {mood.examples}
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="text-center text-sm text-red-400">{error}</p>
      )}

      <div className="flex items-center gap-3">
        {showBackButton && (
          <button onClick={onBack} className="btn-ghost px-5 py-3">
            <ChevronLeft className="h-5 w-5" />
            Back
          </button>
        )}
        <button onClick={handleSubmit} className="btn-marquee flex-1 px-4 py-3.5 text-base">
          Continue
        </button>
      </div>
    </div>
  );
}
